import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { storyAPI } from '../shared/services/api';
import { Story, SearchFilters } from '../shared/types';
import LoadingSpinner from '../components/LoadingSpinner';
import StoryCard from '../features/stories/components/StoryCard';
import { Select } from '../shared/components/UI';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PageTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 3rem;
  font-weight: 700;
  color: var(--color-gray-900);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--color-gray-600);
  max-width: 600px;
  margin: 0 auto;
`;

const SearchContainer = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 2rem;
  margin-bottom: 3rem;
`;

const SearchForm = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SearchLabel = styled.label`
  font-weight: 600;
  color: var(--color-gray-700);
  font-family: var(--font-sans);
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  font-family: var(--font-sans);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const SearchSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  font-family: var(--font-sans);
  background: var(--color-white);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const SearchActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  color: var(--color-gray-700);
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  color: var(--color-gray-500);
`;

const PublicStories: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState<string[]>([]);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    query: '',
    author: '',
    genre: '',
    page: 0,
    size: 12
  });

  // Initialize filters from URL params on mount
  useEffect(() => {
    const initialFilters: SearchFilters = {
      query: searchParams.get('q') || '',
      author: searchParams.get('author') || '',
      genre: searchParams.get('genre') || '',
      page: parseInt(searchParams.get('page') || '0'),
      size: parseInt(searchParams.get('size') || '12')
    };
    setSearchFilters(initialFilters);
  }, []);

  useEffect(() => {
    loadGenres();
  }, []);

  // Search when URL params change
  useEffect(() => {
    const filters: SearchFilters = {
      query: searchParams.get('q') || '',
      author: searchParams.get('author') || '',
      genre: searchParams.get('genre') || '',
      page: parseInt(searchParams.get('page') || '0'),
      size: parseInt(searchParams.get('size') || '12')
    };
    searchStories(filters);
  }, [searchParams]);

  const loadGenres = async () => {
    try {
      const response = await storyAPI.getGenres();
      setGenres(response.data);
    } catch (error) {
      console.error('Failed to load genres:', error);
    }
  };

  const searchStories = async (filters = searchFilters) => {
    setLoading(true);
    try {
      const response = await storyAPI.searchPublicStories(filters);
      setStories(response.data.stories);
    } catch (error) {
      console.error('Failed to search stories:', error);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (field: keyof SearchFilters, value: string | number) => {
    const newFilters = { ...searchFilters, [field]: value, page: 0 };
    setSearchFilters(newFilters);

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(field === 'query' ? 'q' : field, String(value));
    } else {
      newParams.delete(field === 'query' ? 'q' : field);
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const handleSearch = () => {
    // Update URL params to trigger search via useEffect
    const newParams = new URLSearchParams();
    if (searchFilters.query) newParams.set('q', searchFilters.query);
    if (searchFilters.author) newParams.set('author', searchFilters.author);
    if (searchFilters.genre) newParams.set('genre', searchFilters.genre);
    newParams.set('page', String(searchFilters.page));
    newParams.set('size', String(searchFilters.size));
    setSearchParams(newParams);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    const clearFilters = {
      query: '',
      author: '',
      genre: '',
      page: 0,
      size: 12
    };
    setSearchFilters(clearFilters);
    // Clear URL params to trigger search via useEffect
    setSearchParams({});
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Browse Stories</PageTitle>
        <PageSubtitle>
          Discover amazing stories shared by our community of young authors
        </PageSubtitle>
      </PageHeader>

      <SearchContainer>
        <SearchForm>
          <SearchField>
            <SearchLabel htmlFor="query">{t('search.searchButton')}</SearchLabel>
            <SearchInput
              id="query"
              type="text"
              placeholder={t('search.searchPlaceholder')}
              value={searchFilters.query}
              onChange={(e) => handleSearchChange('query', e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </SearchField>

          <SearchField>
            <SearchLabel htmlFor="author">{t('search.filterByAuthor')}</SearchLabel>
            <SearchInput
              id="author"
              type="text"
              placeholder={t('search.authorPlaceholder')}
              value={searchFilters.author}
              onChange={(e) => handleSearchChange('author', e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </SearchField>

          <Select
            label={t('search.filterByGenre')}
            value={searchFilters.genre || ''}
            onChange={(value) => handleSearchChange('genre', value)}
            options={[
              { value: '', label: t('search.allGenres') },
              ...genres.map(genre => ({
                value: genre,
                label: t(`genres.${genre}`, genre)
              }))
            ]}
          />
        </SearchForm>

        <SearchActions>
          <button className="btn btn-primary" onClick={handleSearch}>
            🔍 {t('search.searchButton')}
          </button>
          <button className="btn btn-outline" onClick={handleClear}>
            🗑️ {t('search.clearFilters')}
          </button>
        </SearchActions>
      </SearchContainer>

      {loading ? (
        <LoadingSpinner />
      ) : stories.length > 0 ? (
        <StoriesGrid>
          {stories.map((story) => (
            <StoryCard key={story.id} story={story} showAuthor={true} />
          ))}
        </StoriesGrid>
      ) : (
        <EmptyState>
          <EmptyStateIcon>📖</EmptyStateIcon>
          <EmptyStateTitle>No Stories Found</EmptyStateTitle>
          <EmptyStateText>
            Try adjusting your search filters to find more stories.
          </EmptyStateText>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default PublicStories;